import React, { useState, useEffect } from 'react';

// Types matching the shared categoryFields
interface CategoryField {
  id: string;
  fieldName: string;
  label: string;
  helpText?: string;
  fieldType: 'text' | 'number' | 'boolean' | 'select' | 'multi-select' | 'range';
  isRequired: boolean;
  isFilterable: boolean;
  filterType?: 'range' | 'multi-select' | 'checkbox' | 'discrete';
  options?: { value: string; label: string; count?: number }[];
  min?: number;
  max?: number;
  unit?: string;
  displayOrder: number;
}

interface DynamicFiltersProps {
  categoryId: string;
  filters: CategoryField[];
  activeFilters: Record<string, any>;
  onFilterChange: (filterId: string, value: any) => void;
  onClearAll: () => void;
  productCounts?: Record<string, Record<string, number>>; // { fieldName: { value: count } }
  style?: React.CSSProperties;
}

const DynamicFilters: React.FC<DynamicFiltersProps> = ({
  categoryId,
  filters,
  activeFilters,
  onFilterChange,
  onClearAll,
  productCounts,
  style,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['price']));
  const [rangeValues, setRangeValues] = useState<Record<string, { min: number; max: number }>>({});

  // Initialize expanded sections for filters with active values
  useEffect(() => {
    const activeIds = Object.keys(activeFilters).filter(k => {
      const val = activeFilters[k];
      return val !== undefined && val !== null && (Array.isArray(val) ? val.length > 0 : true);
    });
    setExpandedSections(prev => new Set([...Array.from(prev), ...activeIds]));
  }, [activeFilters]);

  const toggleSection = (fieldId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(fieldId)) {
      newExpanded.delete(fieldId);
    } else {
      newExpanded.add(fieldId);
    }
    setExpandedSections(newExpanded);
  };

  const handleMultiSelectChange = (fieldId: string, value: string, checked: boolean) => {
    const current = activeFilters[fieldId] || [];
    if (checked) {
      onFilterChange(fieldId, [...current, value]);
    } else {
      onFilterChange(fieldId, current.filter((v: string) => v !== value));
    }
  };

  const handleRangeChange = (fieldId: string, type: 'min' | 'max', value: number) => {
    const current = rangeValues[fieldId] || { min: 0, max: 0 };
    const newRange = { ...current, [type]: value };
    setRangeValues({ ...rangeValues, [fieldId]: newRange });
  };

  const applyRangeFilter = (fieldId: string) => {
    const range = rangeValues[fieldId];
    if (range) {
      onFilterChange(fieldId, range);
    }
  };

  const getOptionCount = (fieldId: string, optionValue: string): number => {
    return productCounts?.[fieldId]?.[optionValue] || 0;
  };

  const activeFilterCount = Object.entries(activeFilters).filter(([_, value]) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return value.min !== undefined || value.max !== undefined;
    return value !== false;
  }).length;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      ...style
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '15px',
        borderBottom: '1px solid #eee'
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a1a2e' }}>
          Filters
          {activeFilterCount > 0 && (
            <span style={{
              marginLeft: '8px',
              padding: '2px 8px',
              background: '#ff6b35',
              color: 'white',
              borderRadius: '10px',
              fontSize: '12px'
            }}>
              {activeFilterCount}
            </span>
          )}
        </h3>
        {activeFilterCount > 0 && (
          <button
            onClick={onClearAll}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              color: '#ff6b35',
              border: '1px solid #ff6b35',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Clear All
          </button>
        )}
      </div>

      {/* Filter Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {/* Price Filter (always shown) */}
        <div>
          <button
            onClick={() => toggleSection('price')}
            style={{
              width: '100%',
              padding: '10px 0',
              background: 'none',
              border: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#333'
            }}
          >
            <span>💰 Price</span>
            <span style={{ transform: expandedSections.has('price') ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
          </button>
          {expandedSections.has('price') && (
            <div style={{ padding: '10px 0' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={rangeValues['price']?.min || ''}
                  onChange={(e) => handleRangeChange('price', 'min', parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}
                />
                <span style={{ color: '#999' }}>—</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={rangeValues['price']?.max || ''}
                  onChange={(e) => handleRangeChange('price', 'max', parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}
                />
              </div>
              <button
                onClick={() => applyRangeFilter('price')}
                style={{
                  width: '100%',
                  marginTop: '10px',
                  padding: '8px',
                  background: '#f0f0f0',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Dynamic Category-Specific Filters */}
        {filters.map(filter => (
          <div key={filter.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: '15px' }}>
            <button
              onClick={() => toggleSection(filter.id)}
              style={{
                width: '100%',
                padding: '10px 0',
                background: 'none',
                border: 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#333'
              }}
            >
              <span>
                {filter.label}
                {filter.unit && <span style={{ fontSize: '11px', color: '#999', marginLeft: '5px' }}>({filter.unit})</span>}
              </span>
              <span style={{ transform: expandedSections.has(filter.id) ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {expandedSections.has(filter.id) && (
              <div style={{ padding: '10px 0' }}>
                {/* Multi-Select / Discrete Filter */}
                {(filter.filterType === 'multi-select' || filter.filterType === 'discrete') && filter.options && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {filter.options.map(option => {
                      const count = getOptionCount(filter.id, option.value);
                      const isChecked = (activeFilters[filter.id] || []).includes(option.value);
                      return (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: '6px',
                            background: isChecked ? '#fff5f0' : 'transparent',
                            transition: 'background 0.2s'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleMultiSelectChange(filter.id, option.value, e.target.checked)}
                            style={{ width: '16px', height: '16px', accentColor: '#ff6b35' }}
                          />
                          <span style={{ flex: 1, fontSize: '13px', color: isChecked ? '#ff6b35' : '#333' }}>
                            {option.label}
                          </span>
                          {count > 0 && (
                            <span style={{ fontSize: '11px', color: '#999', background: '#f5f5f5', padding: '2px 6px', borderRadius: '10px' }}>
                              {count}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Range Filter */}
                {filter.filterType === 'range' && (
                  <div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="number"
                        placeholder={`Min ${filter.unit || ''}`}
                        value={rangeValues[filter.id]?.min || ''}
                        onChange={(e) => handleRangeChange(filter.id, 'min', parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}
                      />
                      <span style={{ color: '#999' }}>—</span>
                      <input
                        type="number"
                        placeholder={`Max ${filter.unit || ''}`}
                        value={rangeValues[filter.id]?.max || ''}
                        onChange={(e) => handleRangeChange(filter.id, 'max', parseFloat(e.target.value))}
                        style={{
                          flex: 1,
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                    <button
                      onClick={() => applyRangeFilter(filter.id)}
                      style={{
                        width: '100%',
                        marginTop: '10px',
                        padding: '8px',
                        background: '#f0f0f0',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Apply
                    </button>
                  </div>
                )}

                {/* Checkbox Filter */}
                {filter.filterType === 'checkbox' && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      padding: '6px 8px'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={activeFilters[filter.id] === true}
                      onChange={(e) => onFilterChange(filter.id, e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#ff6b35' }}
                    />
                    <span style={{ fontSize: '13px', color: activeFilters[filter.id] ? '#ff6b35' : '#333' }}>
                      Yes, include {filter.label.toLowerCase()}
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Filter Info */}
      <div style={{
        marginTop: '20px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '8px',
        fontSize: '11px',
        color: '#666',
        textAlign: 'center'
      }}>
        Filters update in real-time based on available products
      </div>
    </div>
  );
};

export default DynamicFilters;
